import { useState, useEffect} from 'react';
import { Form, Card, Row, Col, Button, Image } from 'react-bootstrap';
import { validateURL, validateField, validateEmail } from './utils/validation'; // ✅
import icLink from './assets/img/ic-link-square.svg';
import icMail from './assets/img/ic-mail-send.svg';
import icPhone from './assets/img/ic-smart-phone.svg';
import icValidationError from './assets/img/ic-danger.svg';

const CompanyAddHr = ({ showErrorMessage, setShowErrorMessage, formData, handleChange, errors = {}, handleHrSubmit, isEditPage }) => { // ✅ Ensure formData & errors have default values

    const [isEditing, setIsEditing] = useState(!isEditPage); // ✅ If adding, fields are enabled
    const [tempFormData, setTempFormData] = useState({ ...formData }); // ✅ Store initial values
    const [localHrRecrutingUrl, setLocalHrRecrutingUrl] = useState(formData.hrRecrutingUrl || ''); // ✅ Sync initial state
    const [localErrors, setLocalErrors] = useState({}); // ✅ Renamed errors state to avoid conflict
    const [localEmailErrors, setLocalEmailErrors] = useState({}); // ✅
    const [localHrEmail, setLocalHrEmail] = useState(formData.hrEmail || '')

    
    // ✅ Enable editing mode
    const handleEditClick = () => {
        setIsEditing(true);
        setTempFormData({ ...formData }); // ✅ Store previous values
    };

    // ✅ Save changes and disable editing
    const handleSaveClick = async (e) => {
        e.preventDefault();

        const trimmedUrl = localHrRecrutingUrl.trim();
        const trimmedEmail = localHrEmail.trim();

        // ✅ Re-validate URL and email before saving (use direct results, not stale async state)
        const isUrlValid = !trimmedUrl || validateURL(trimmedUrl);
        const isEmailValid = !trimmedEmail || validateEmail(trimmedEmail);

        validateField('url', trimmedUrl, validateURL, setLocalErrors, 'Please enter a valid URL.');
        validateField('email', trimmedEmail, validateEmail, setLocalEmailErrors, 'Please enter a valid email address.');

        if (!isUrlValid) {
            console.log('Cannot save: URL is invalid.');
            setShowErrorMessage(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        if (!isEmailValid) {
            console.log('Cannot save: Email is invalid.');
            setShowErrorMessage(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        // ✅ Cancel any pending debounced sync and submit the current values directly,
        // so editing an existing value and saving quickly doesn't get overwritten by stale parent state.
        clearTimeout(window.urlValidationTimeout);
        clearTimeout(window.emailValidationTimeout);

        if (typeof handleHrSubmit === 'function') {
            await handleHrSubmit(e, { hrRecrutingUrl: trimmedUrl, hrEmail: trimmedEmail });
            setShowErrorMessage(false);
            setIsEditing(false);
        } else {
            console.error('Error: handleHrSubmit is not a function.');
        }
    };



    // ✅ Cancel editing and restore previous values
    const handleCancelClick = () => {
        setIsEditing(false);
        Object.keys(tempFormData).forEach((key) => {
            handleChange({ target: { name: key, value: tempFormData[key] } });
        });
        setLocalHrRecrutingUrl(tempFormData.hrRecrutingUrl || ''); // ✅ Restore local state
    };

    useEffect(() => {
        setLocalHrRecrutingUrl(formData.hrRecrutingUrl || '');
        setLocalHrEmail(formData.hrEmail || '');
    }, [formData.hrRecrutingUrl, formData.hrEmail]);

    // Handle URL field change
    const handleURLChange = (e) => {
        const { value } = e.target;
        setLocalHrRecrutingUrl(value);

        clearTimeout(window.urlValidationTimeout);
        window.urlValidationTimeout = setTimeout(() => {
            validateField('url', value.trim(), validateURL, setLocalErrors, 'Please enter a valid URL.');
            if (validateURL(value.trim())) {
                handleChange({ target: { name: 'hrRecrutingUrl', value } });
            }
        }, 800);
    };

    const handleEmailChange = (e) => {
        const { value } = e.target;
        setLocalHrEmail(value);

        clearTimeout(window.emailValidationTimeout);
        window.emailValidationTimeout = setTimeout(() => {
            if (value.trim() === '') {
                setLocalEmailErrors((prev) => ({ ...prev, email: '' }));
                handleChange({ target: { name: 'hrEmail', value: '' } });
            } else {
                validateField('email', value.trim(), validateEmail, setLocalEmailErrors, 'Please enter a valid email address.');
                if (validateEmail(value.trim())) {
                    handleChange({ target: { name: 'hrEmail', value } });
                }
            }
        }, 800);
    };

    return (
        <> 
            <Card className="mb-4">
                <Card.Body>
                    <Row>
                        <Col md={12}>
                            {isEditPage && (
                                <div className="float-end">
                                    {!isEditing ? (
                                        <Button variant="outline-secondary" className="btn-sm" onClick={handleEditClick}>Edit</Button>
                                    ) : (
                                        <>
                                            <Button variant="outline-secondary" className="btn-sm me-2" onClick={handleCancelClick}>Cancel</Button>
                                            <Button variant="primary" className="btn-sm" onClick={handleSaveClick}>Save</Button>
                                        </>
                                    )}
                                </div>
                            )}
                            <h3><span className="item-number">1.</span> HR:&nbsp;<span>{formData.hrFullName || ''}</span></h3>
                            <hr />
                        </Col>
                    </Row>
                    <>
                        {isEditing ? (
                            <>
                                <Row>
                                    <Col md={3}>
                                        {/* HR Full Name */}
                                        <Form.Group controlId="hrFullName">
                                            <Form.Label>HR Full Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="hrFullName"
                                                value={formData.hrFullName || ''} // ✅ Ensure fallback value
                                                onChange={handleChange}
                                                disabled={!isEditing} // ✅ Disable input when not in edit mode
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        {/* Recruiting portal URL */}
                                        <Form.Group className="validated" controlId="hrRecrutingUrl">
                                            <Form.Label>Recruiting portal URL</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="hrRecrutingUrl"
                                                value={localHrRecrutingUrl}
                                                onChange={handleURLChange}
                                                isInvalid={!!localErrors.url}
                                                disabled={!isEditing} // ✅ Disable input when not in edit mode
                                            />
                                            {/* ✅ Custom error icon + tooltip */}
                                            {localErrors.url && (
                                                <div className="custom-error-icon-wrapper">
                                                    <img src={icValidationError} alt={localErrors.url} className="custom-error-icon" />
                                                    <div className="custom-tooltip">
                                                        {localErrors.url}
                                                    </div>
                                                </div>
                                            )}
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        {/* HR email */}
                                        <Form.Group className="validated" controlId="hrEmail">
                                            <Form.Label>HR email</Form.Label>
                                            <Form.Control
                                                type="email"
                                                name="hrEmail"
                                                value={localHrEmail}
                                                onChange={handleEmailChange}
                                                isInvalid={!!localEmailErrors.email}
                                                disabled={!isEditing}
                                            />
                                            {localEmailErrors.email && (
                                                <div className="custom-error-icon-wrapper">
                                                    <img src={icValidationError} alt={localEmailErrors.email} className="custom-error-icon" />
                                                    <div className="custom-tooltip">
                                                        {localEmailErrors.email}
                                                    </div>
                                                </div>
                                            )}
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        {/* HR phone */}
                                        <Form.Group controlId="hrPhone">
                                            <Form.Label>HR phone</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="hrPhone"
                                                value={formData.hrPhone || ''} // ✅ Ensure fallback value
                                                onChange={handleChange}
                                                disabled={!isEditing} // ✅ Disable input when not in edit mode
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </>
                        ) :(
                            // Not editing
                            <>
                                <Row>
                                    <Col md={4}>
                                        <div className="icon-text">
                                            <span className="icon" aria-hidden="true">
                                                <Image src={icLink} alt="Recruiting portal URL" />
                                            </span>
                                            <span className="text">
                                                Recruiting portal URL
                                                <br />
                                                <strong>
                                                    {localHrRecrutingUrl ? (
                                                        <a
                                                            href={`https://www.${localHrRecrutingUrl}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            {localHrRecrutingUrl}
                                                        </a>
                                                    ) : (
                                                        "N/A"
                                                    )}
                                                </strong>
                                            </span>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="icon-text">
                                            <span className="icon" aria-hidden="true">
                                                <Image src={icMail} alt="HR email" />
                                            </span>
                                            <span className="text">
                                                HR email:
                                                <br />
                                                <strong>
                                                    {formData.hrEmail ? (
                                                        <a href={`mailto:${formData.hrEmail}`}>
                                                            {formData.hrEmail}
                                                        </a>
                                                    ) : (
                                                        "N/A"
                                                    )}
                                                </strong>
                                            </span>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="icon-text">
                                            <span className="icon" aria-hidden="true">
                                                <Image src={icPhone} alt="HR phone" />
                                            </span>
                                            <span className="text">
                                                HR phone:
                                                <br />
                                                <strong>
                                                    {formData.hrPhone || "N/A"}
                                                </strong>
                                            </span>
                                        </div>
                                    </Col>
                                </Row>
                            </>
                        )}
                    </>
                </Card.Body>
            </Card>
        </>
    );
};

export default CompanyAddHr;
